import { Alert, Table, Button, Modal } from "antd";
import { GetProfileDto } from "../../../services/client";
import {
  listProfilesInfiniteOptions,
  deleteProfileMutation,
  listProfilesQueryKey,
} from "../../../services/client/@tanstack/react-query.gen";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
  FETCH_PAGE_SIZE,
  useCursorPagination,
} from "../../../hooks/useCursorPagination";
import { useNotify } from "../../util-components/Notifications/notifications";
import { ProfileForm } from "./ProfileForm";
import { DisableForViewer } from "../../common/DisableForViewer";

type ProfileListProps = {
  site_id: string;
};

export const ProfilesList: React.FC<ProfileListProps> = ({ site_id }) => {
  const [profileToDelete, setProfileToDelete] = useState<GetProfileDto | null>(
    null,
  );
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [profileToUpdate, setProfileToUpdate] = useState<GetProfileDto | null>(
    null,
  );

  const notify = useNotify();
  const queryClient = useQueryClient();

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, error } =
    useInfiniteQuery({
      ...listProfilesInfiniteOptions({
        path: { site_id },
        query: { page_size: FETCH_PAGE_SIZE },
      }),
      getNextPageParam: (lastPage) => lastPage.end_cursor,
      enabled: !!site_id,
    });

  const deleteMutation = useMutation({
    ...deleteProfileMutation(),
    onSuccess: () => {
      // Invalidate and refetch the profiles list
      queryClient.invalidateQueries({
        queryKey: listProfilesQueryKey({ path: { site_id } }),
      });
      notify({
        type: "success",
        message: "Profile Deleted",
        description: "The profile has been successfully deleted.",
      });
      setIsDeleteModalVisible(false);
      setProfileToDelete(null);
    },
    onError: () => {
      notify({
        type: "error",
        message: "Delete Failed",
        description:
          "There was an error deleting the profile. Please try again.",
      });
    },
  });

  const handleDeleteClick = (profile: GetProfileDto) => {
    setProfileToDelete(profile);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    if (profileToDelete) {
      deleteMutation.mutate({
        path: {
          site_id,
          profile_id: profileToDelete.profile_id,
        },
      });
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setProfileToDelete(null);
  };

  const handleUpdateClick = (profile: GetProfileDto) => {
    setProfileToUpdate(profile);
  };

  const handleUpdateSuccess = () => {
    setProfileToUpdate(null);
  };

  const handleUpdateCancel = () => {
    setProfileToUpdate(null);
  };

  const columns: ColumnsType<GetProfileDto> = useMemo(
    () => [
      { title: "Profile ID", dataIndex: "profile_id", key: "profile_id" },
      { title: "Description", dataIndex: "description", key: "description" },
      {
        title: "AC",
        children: [
          {
            title: "Default Departure",
            key: "ac_default_estimated_departure_time_delta",
            render: (_, profile) =>
              profile.ac?.default_estimated_departure_time_delta ?? "-",
          },
          {
            title: "Min Energy (Wh)",
            key: "ac_default_requested_min_energy",
            render: (_, profile) =>
              profile.ac?.default_requested_min_energy ?? "-",
          },
          {
            title: "Max Energy (Wh)",
            key: "ac_default_requested_max_energy",
            render: (_, profile) =>
              profile.ac?.default_requested_max_energy ?? "-",
          },
        ],
      },
      {
        title: "DC",
        children: [
          {
            title: "Default Departure",
            key: "dc_default_estimated_departure_time_delta",
            render: (_, profile) =>
              profile.dc?.default_estimated_departure_time_delta ?? "-",
          },
          {
            title: "Min SoC (%)",
            key: "dc_default_requested_min_state_of_charge",
            render: (_, profile) =>
              profile.dc?.default_requested_min_state_of_charge ?? "-",
          },
          {
            title: "Max SoC (%)",
            key: "dc_default_requested_max_state_of_charge",
            render: (_, profile) =>
              profile.dc?.default_requested_max_state_of_charge ?? "-",
          },
        ],
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, profile) => (
          <div className="flex gap-2">
            <DisableForViewer>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleUpdateClick(profile)}
                title="Edit profile"
              />
            </DisableForViewer>
            <DisableForViewer>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteClick(profile)}
                title="Delete profile"
              />
            </DisableForViewer>
          </div>
        ),
      },
    ],
    [],
  );

  const { dataSource, pagination, isPageLoading } =
    useCursorPagination<GetProfileDto>({
      pages: data?.pages,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    });

  return (
    <div className="p-4 w-full">
      {!!error && (
        <Alert
          message="Error"
          description="There was an issue loading the profiles data."
          type="error"
          showIcon
        />
      )}

      {data && (
        <>
          <Alert
            message={
              <p>
                <strong>Profiles</strong> are used to set the default values for
                requested energy and for the estimated departure time of a
                transaction.
                <br /> You can find more information on how this works in the{" "}
                <a href="https://api-docs.pleevi.ai/tutorial/key-concepts/default-profiles">
                  documentation
                </a>
                .
              </p>
            }
            type="info"
            showIcon
            className="mb-4"
          />

          <Table<GetProfileDto>
            dataSource={dataSource}
            columns={columns}
            rowKey="profile_id"
            loading={isPageLoading}
            pagination={pagination}
          />
        </>
      )}

      <Modal
        title="Delete Profile"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        okType="danger"
        confirmLoading={deleteMutation.isPending}
        cancelText="Cancel"
      >
        {profileToDelete && (
          <div>
            <p>
              Are you sure you want to delete this profile? This action cannot
              be undone.
            </p>
            <div className="bg-gray-50 p-3 rounded mt-3">
              <p>
                <strong>Profile ID:</strong> {profileToDelete.profile_id}
              </p>
              {profileToDelete.description && (
                <p>
                  <strong>Description:</strong> {profileToDelete.description}
                </p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Update Profile"
        open={profileToUpdate != null}
        onCancel={handleUpdateCancel}
        footer={null}
        width={600}
      >
        {profileToUpdate && (
          <ProfileForm
            key={profileToUpdate.profile_id}
            site_id={site_id}
            mode="update"
            profile={profileToUpdate}
            onSuccess={handleUpdateSuccess}
          />
        )}
      </Modal>
    </div>
  );
};
